import { HStack, useToken } from "@chakra-ui/react"
import ProductFeaturesItem from "@components/Product/ProductFeaturesItem"
import { convertDateFormat } from "@utils/date"
import { AiOutlineBarcode } from "react-icons/ai"
import { BsBuildingsFill } from "react-icons/bs"
import { FaGraduationCap, FaRegCalendarAlt } from "react-icons/fa"
import { HiLanguage } from "react-icons/hi2"
import { ImBarcode } from "react-icons/im"
import { MdFace, MdMenuBook } from "react-icons/md"
import { SiBookstack } from "react-icons/si"

type ProductFeaturesProps = {
  data: {
    audience?: { description: string }
    educationalLevel?: {
      inDefinedTermSet: string
      name: string
    }
    isPartOf?: {
      name: string
    }[]
    numberOfPages?: number
    inLanguage?: string
    publisher?: string
    datePublished?: string
    isbn?: string
    gtin: string
  }
}

const ProductFeatures = ({ data }: ProductFeaturesProps) => {
  const [yellow500, gray50] = useToken("colors", ["yellow.500", "gray.50"])

  return (
    <HStack
      width={"full"}
      borderTop={"1px solid"}
      borderBottom={"1px solid"}
      borderColor={"gray.200"}
      py={4}
      alignItems={"flex-start"}
      spacing={4}
      overflowX={"auto"}
      sx={{
        scrollbarColor: `${yellow500} transparent`,
      }}
      css={{
        "&::-webkit-scrollbar": {
          height: "2px",
        },
        "&::-webkit-scrollbar-track": {
          height: "2px",
          backgroundColor: gray50,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: yellow500,
          borderRadius: "6px",
        },
      }}
    >
      {data.audience?.description && (
        <ProductFeaturesItem
          icon={MdFace}
          title={"Reading Age"}
          value={data.audience.description}
        />
      )}

      {data.educationalLevel?.name &&
        data.educationalLevel?.inDefinedTermSet && (
          <ProductFeaturesItem
            icon={FaGraduationCap}
            title={data.educationalLevel.inDefinedTermSet}
            value={data.educationalLevel.name}
          />
        )}

      {data.isPartOf &&
        data.isPartOf?.length > 0 &&
        data.isPartOf?.map((series, index) => (
          <ProductFeaturesItem
            key={index}
            icon={SiBookstack}
            title={"Part of Series"}
            url={`/s?products[refinementList][isPartOf.en_AE][0]=${encodeURIComponent(
              series.name
            )}`}
            value={series.name}
          />
        ))}

      {data.numberOfPages && (
        <ProductFeaturesItem
          icon={MdMenuBook}
          title={"Print Length"}
          value={`${data.numberOfPages} pages`}
        />
      )}

      {data.inLanguage && (
        <ProductFeaturesItem
          icon={HiLanguage}
          title={"Language"}
          value={data.inLanguage}
          url={`/s?products[refinementList][inLanguage][0]=${encodeURIComponent(
            data.inLanguage
          )}`}
        />
      )}

      {data.publisher && (
        <ProductFeaturesItem
          icon={BsBuildingsFill}
          title={"Publisher"}
          value={data.publisher}
          url={`/s?products[refinementList][publisher.en_AE][0]=${encodeURIComponent(
            data.publisher
          )}`}
        />
      )}

      {data.datePublished && (
        <ProductFeaturesItem
          icon={FaRegCalendarAlt}
          title={"Date Published"}
          value={convertDateFormat(String(data.datePublished), {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        />
      )}

      {data.isbn && (
        <ProductFeaturesItem
          icon={AiOutlineBarcode}
          title={"ISBN-10"}
          value={data.isbn}
        />
      )}

      <ProductFeaturesItem
        icon={ImBarcode}
        title={"ISBN-13"}
        value={data.gtin}
      />
    </HStack>
  )
}

export default ProductFeatures
